import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Divider,
    Fade,
    FormControlLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Slide,
    Switch,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useLogout, useTheme } from 'react-admin';
import type { MenuEntry } from '../menuEntries';
import {
    MORE_SHEET_ID,
    SAFE_AREA_LEFT,
    SAFE_AREA_RIGHT,
    TAB_BAR_HEIGHT_PX,
    TOP_BAR_ATTRIBUTE,
    phoneSurface,
} from '../chrome';
import { DOCK_ATTRIBUTE } from '../useEditingOutsideDock';
import { useDocumentScrollLock } from '../useDocumentScrollLock';
import {
    AdminIcon,
    ChevronDownIcon,
    LogoutIcon,
    MoonIcon,
    entryIcon,
} from './icons';
import {
    ImprintDialog,
    useImprintDialog,
} from '../../../components/ImprintDialog';
import {
    PrivacyDialog,
    usePrivacyDialog,
} from '../../../components/PrivacyDialog';
import {
    LegalDocuments,
    useLegalDocuments,
} from '../../../legal/LegalDocuments';

interface MoreSheetProps {
    open: boolean;
    onClose: () => void;
    entries: MenuEntry[];
    /**
     * The tab bar stays visible (its More has turned into Close): the sheet and
     * the scrim end where it starts instead of reaching the screen edge.
     */
    aboveTabBar?: boolean;
    /** The sheet has finished sliding out and is gone (not when it starts to close). */
    onExited?: () => void;
}

// A row as drawn: 52 px, 20 px at the sides, a 24 px icon and 16 px to the text.
const ROW = { minHeight: 52, px: '20px', gap: '16px' } as const;
const ICON = { minWidth: 0, color: 'text.secondary' } as const;
// Admin entries sit one step in under the Admin row.
const NESTED = { ...ROW, pl: '36px' } as const;

function EntryRow({
    entry,
    onClose,
    nested,
}: {
    entry: MenuEntry;
    onClose: () => void;
    nested?: boolean;
}) {
    const Icon = entryIcon(entry);
    const sx = nested ? NESTED : ROW;
    const content = (
        <>
            <ListItemIcon sx={ICON}>
                <Icon />
            </ListItemIcon>
            <ListItemText primary={entry.label} />
        </>
    );
    if (entry.disabled || !entry.to) {
        return (
            <ListItemButton disabled sx={sx}>
                {content}
            </ListItemButton>
        );
    }
    return (
        <ListItemButton
            component={Link}
            to={entry.to}
            onClick={onClose}
            sx={sx}
        >
            {content}
        </ListItemButton>
    );
}

// Phone counterpart of the desktop user menu, opened from the tab bar's More.
// Also carries the footer links, which would sit under the tab bar on phones.
//
// A sheet on the page, not a modal: MUI's Modal hides the rest of the page from
// assistive technology and covers the tab bar, and the mockup keeps the tab bar
// (with More turned into Close) under the sheet. So: a scrim, the sheet, Escape
// and a tap on the scrim to close, and focus moved in and given back.
export function MoreSheet({
    open,
    onClose,
    entries,
    aboveTabBar = false,
    onExited,
}: MoreSheetProps) {
    const [theme, setTheme] = useTheme();
    const muiTheme = useMuiTheme();
    const logout = useLogout();
    const { imprintOpen, openImprint, closeImprint } = useImprintDialog();
    const { privacyOpen, openPrivacy, closePrivacy } = usePrivacyDialog();
    const { legalDocumentsOpen, openLegalDocuments, closeLegalDocuments } =
        useLegalDocuments();
    const [adminOpen, setAdminOpen] = useState(false);
    const sheetRef = useRef<HTMLDivElement>(null);

    // The sheet opens folded: reset as it opens (before it is painted), not as
    // it closes, so nothing changes in it while it slides out.
    useLayoutEffect(() => {
        if (open) setAdminOpen(false);
    }, [open]);

    // The newest onClose, read when Escape is pressed. The effect below must
    // depend on `open` alone: a parent that re-renders with a new function (a
    // theme switch in this very sheet does that) would otherwise move focus
    // out of the sheet and back, and the switch would lose it.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    // Read when it opens, like onClose: a change while it is open must not
    // re-run the effect below.
    const aboveTabBarRef = useRef(aboveTabBar);
    useEffect(() => {
        aboveTabBarRef.current = aboveTabBar;
    });

    // The page behind does not scroll under a drag on the sheet or the dim.
    useDocumentScrollLock(open);

    // While it is open: Escape closes it; focus goes into the sheet and back to
    // where it was; and what the dim covers (the page, the top bar) is inert,
    // out of reach of Tab and of assistive technology (a sheet that is not a
    // modal does not do that by itself). With a tab bar, the dock stays live
    // (its Close is there); without one the sheet covers the dock's search row,
    // and the row is inert too.
    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement as HTMLElement | null;
        const covered = [
            document.getElementById('main-content'),
            document.querySelector<HTMLElement>(`[${TOP_BAR_ATTRIBUTE}]`),
            aboveTabBarRef.current
                ? null
                : document.querySelector<HTMLElement>(`[${DOCK_ATTRIBUTE}]`),
        ].filter((el): el is HTMLElement => el !== null);
        covered.forEach(el => el.setAttribute('inert', ''));
        sheetRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            // Inert off first: focus cannot go back to a button inside it.
            covered.forEach(el => el.removeAttribute('inert'));
            if (previous && document.contains(previous)) previous.focus();
        };
    }, [open]);

    const LEGAL_LINKS = [
        { label: 'Privacy', open: openPrivacy },
        { label: 'Imprint', open: openImprint },
        { label: 'Legal', open: openLegalDocuments },
    ];
    const main = entries.filter(e => e.group === 'main');
    const admin = entries.filter(e => e.group === 'admin');

    // Where the tab bar starts: its height and the home indicator under it.
    const aboveBar = `calc(${TAB_BAR_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;
    const layer = muiTheme.zIndex.drawer;

    return (
        <>
            <Fade in={open} unmountOnExit>
                <Box
                    data-testid="more-scrim"
                    aria-hidden
                    onClick={onClose}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: aboveTabBar ? aboveBar : 0,
                        bgcolor: 'rgba(0, 0, 0, 0.45)',
                        // A drag on the dim does not scroll the page behind it.
                        touchAction: 'none',
                        zIndex: layer - 1,
                    }}
                />
            </Fade>
            <Slide
                direction="up"
                in={open}
                mountOnEnter
                unmountOnExit
                onExited={onExited}
            >
                <Paper
                    ref={sheetRef}
                    id={MORE_SHEET_ID}
                    role="dialog"
                    aria-label="More"
                    tabIndex={-1}
                    elevation={0}
                    square
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: aboveTabBar ? aboveBar : 0,
                        zIndex: layer,
                        bgcolor: phoneSurface(muiTheme),
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        pt: 1,
                        pb: aboveTabBar
                            ? 1
                            : 'calc(8px + env(safe-area-inset-bottom, 0px))',
                        pl: SAFE_AREA_LEFT,
                        pr: SAFE_AREA_RIGHT,
                        maxHeight: aboveTabBar
                            ? `calc(85dvh - ${aboveBar})`
                            : '85dvh',
                        overflowY: 'auto',
                        // ... and neither does the end of the sheet's own scroll.
                        overscrollBehavior: 'contain',
                        outline: 'none',
                    }}
                >
                    <Box
                        data-testid="more-handle"
                        sx={{
                            width: 36,
                            height: 4,
                            borderRadius: '2px',
                            // 24% of the text colour: #c2c2c2 on the white sheet,
                            // a light grey on the dark one.
                            bgcolor: alpha(muiTheme.palette.text.primary, 0.24),
                            mx: 'auto',
                            mb: 1,
                        }}
                    />
                    <List aria-label="More" disablePadding>
                        {main.map(entry => (
                            <EntryRow
                                key={entry.id}
                                entry={entry}
                                onClose={onClose}
                            />
                        ))}
                        {admin.length > 0 && (
                            <>
                                <ListItemButton
                                    sx={ROW}
                                    aria-expanded={adminOpen}
                                    onClick={() => setAdminOpen(o => !o)}
                                >
                                    <ListItemIcon sx={ICON}>
                                        <AdminIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Admin" />
                                    <ChevronDownIcon
                                        sx={{
                                            color: 'text.secondary',
                                            transform: adminOpen
                                                ? 'rotate(180deg)'
                                                : 'none',
                                            transition: 'transform 150ms',
                                        }}
                                    />
                                </ListItemButton>
                                {adminOpen &&
                                    admin.map(entry => (
                                        <EntryRow
                                            key={entry.id}
                                            entry={entry}
                                            onClose={onClose}
                                            nested
                                        />
                                    ))}
                            </>
                        )}
                        <Divider
                            component="li"
                            sx={{
                                my: '4px',
                                borderColor: alpha(
                                    muiTheme.palette.text.primary,
                                    0.08
                                ),
                            }}
                        />
                        <ListItem sx={ROW}>
                            <ListItemIcon sx={ICON}>
                                <MoonIcon />
                            </ListItemIcon>
                            <FormControlLabel
                                label="Dark theme"
                                labelPlacement="start"
                                sx={{
                                    flex: 1,
                                    m: 0,
                                    minHeight: 44,
                                    justifyContent: 'space-between',
                                }}
                                control={
                                    <Switch
                                        edge="end"
                                        checked={theme === 'dark'}
                                        onChange={() =>
                                            setTheme(
                                                theme === 'dark'
                                                    ? 'light'
                                                    : 'dark'
                                            )
                                        }
                                    />
                                }
                            />
                        </ListItem>
                        <ListItemButton
                            sx={ROW}
                            onClick={() => {
                                onClose();
                                logout();
                            }}
                        >
                            <ListItemIcon sx={ICON}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary="Log out" />
                        </ListItemButton>
                    </List>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '4px',
                            px: '20px',
                            pt: '6px',
                            pb: '2px',
                        }}
                    >
                        {LEGAL_LINKS.map(({ label, open: openLink }) => (
                            <Button
                                key={label}
                                color="inherit"
                                onClick={openLink}
                                sx={{
                                    minHeight: 44,
                                    minWidth: 0,
                                    px: 1,
                                    fontSize: 13,
                                    fontWeight: 400,
                                    letterSpacing: 'normal',
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Paper>
            </Slide>
            <ImprintDialog open={imprintOpen} onClose={closeImprint} />
            <PrivacyDialog open={privacyOpen} onClose={closePrivacy} />
            <LegalDocuments
                open={legalDocumentsOpen}
                onClose={closeLegalDocuments}
            />
        </>
    );
}
